import { Controller } from "@hotwired/stimulus"

// Tab History Controller
// Manages browser history for product tab navigation
// First tab switch: advance (push to history) - allows one back navigation
// Subsequent switches: replace (no history push) - prevents stack pollution
// Same URL click: prevent navigation, scroll to top only (no image blur reset)
// Optimistic UI: classList-based active/inactive tab toggle on click (no server wait)
//
// Usage: data-controller="tab-history" on nav element
//        data-tab-history-target="tab" on each tab link
//        data-action="click->tab-history#handleClick" on each tab link
export default class extends Controller {
  static targets = ["tab"]

  handleClick(event) {
    const tab = event.currentTarget
    const href = tab.href || tab.getAttribute('href')

    // 같은 URL 클릭 감지: 페이지 새로고침 방지 (이미지 blur 리셋 방지)
    if (this.isSameUrl(href)) {
      event.preventDefault()
      window.scrollTo({ top: 0, behavior: 'smooth' })
      return
    }

    // Native shells turn a Turbo visit into a pushed native screen; keep tab switches inside the frame there.
    if (document.documentElement.classList.contains('turbo-native')) {
      this.updateActiveTab(tab)
      window.scrollTo({ top: 0, behavior: 'auto' })
      return
    }

    const hasNavigated = sessionStorage.getItem('productTabHasNavigated')

    if (hasNavigated) {
      // Already navigated via product tabs → replace (history swap)
      tab.dataset.turboAction = 'replace'
    } else {
      // First tab navigation → advance (history push)
      tab.dataset.turboAction = 'advance'
      sessionStorage.setItem('productTabHasNavigated', 'true')
    }

    // Optimistic UI: classList 기반 토글 (ripple 등 동적 클래스 보존)
    this.updateActiveTab(tab)

    // Scroll to top for new tab content
    window.scrollTo({ top: 0, behavior: 'auto' })
  }

  // classList 기반 토글 — className 덮어쓰기 대신 개별 클래스 추가/제거
  updateActiveTab(activeTab) {
    const activeClasses = (activeTab.dataset.activeClass || '').split(/\s+/).filter(Boolean)
    const inactiveClasses = (activeTab.dataset.inactiveClass || '').split(/\s+/).filter(Boolean)
    if (!activeClasses.length || !inactiveClasses.length) return

    this.tabTargets.forEach(tab => {
      if (tab === activeTab) {
        tab.classList.remove(...inactiveClasses)
        tab.classList.add(...activeClasses)
        tab.setAttribute('aria-current', 'page')
      } else {
        tab.classList.remove(...activeClasses)
        tab.classList.add(...inactiveClasses)
        tab.removeAttribute('aria-current')
      }
    })
  }

  // URL 비교 헬퍼 (pathname + query string)
  isSameUrl(href) {
    const currentUrl = new URL(window.location.href)
    const targetUrl = new URL(href, window.location.origin)
    return currentUrl.pathname === targetUrl.pathname &&
           currentUrl.search === targetUrl.search
  }
}
